import { KindeProvider } from "@kinde-oss/kinde-auth-react";
import { lazy, StrictMode, Suspense, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router";

import { ErrorBoundary } from "@/components/error-boundary/ErrorBoundary";
import {
	LoadingOverlay,
	SuspenseTracker,
} from "@/components/loading-overlay/loading-overlay";
import { Toaster } from "@/components/notifications/toaster";
import { ROUTES } from "@/constants/routes";
import { NavigationFeatureFlagsProvider } from "@/context/navigation-feature-flags";
import { WebSocketProvider } from "@/context/websocket";
import { PermissionGuard, ProtectedLayout } from "@/features/auth/auth-guard";
import { useNavigationFeatureFlags } from "@/hooks/useNavigationFeatureFlags";
import { PERMISSIONS } from "@/hooks/usePermissions";
import { PostHogIdentity } from "@/lib/posthog-identity";
import { installStaleChunkReload } from "@/lib/stale-chunk-reload";
import { EventFiltersProvider } from "@/providers/event-filters-provider";
import { QueryProvider } from "@/providers/query-provider";
import "@/styles/globals.css";
import { RouteProvider } from "@/providers/router-provider";
import { SettingsProvider } from "@/providers/settings-provider";

import { AuthLoginRedirect } from "./features/auth/auth-login-redirect";

// Lazy load all route components for code-splitting
// This ensures components are only loaded when their routes are accessed
const Analytics = lazy(() =>
	import("@/pages/Analytics").then((m) => ({ default: m.Analytics })),
);
const Dashboard = lazy(() =>
	import("@/pages/Dashboard").then((m) => ({ default: m.Dashboard })),
);
const EventDetails = lazy(() =>
	import("@/pages/Events/EventDetails").then((m) => ({
		default: m.EventDetails,
	})),
);
const Login = lazy(() =>
	import("@/pages/Login").then((m) => ({ default: m.Login })),
);
const NotFound = lazy(() =>
	import("@/pages/NotFound").then((m) => ({ default: m.NotFound })),
);
const LocationEdit = lazy(() =>
	import("@/pages/Settings/LocationManagement/LocationEdit").then((m) => ({
		default: m.LocationEdit,
	})),
);
const LocationsList = lazy(() =>
	import("@/pages/Settings/LocationManagement/LocationsList").then((m) => ({
		default: m.LocationsList,
	})),
);
const UserEdit = lazy(() =>
	import("@/pages/Settings/UserManagement/UserEdit").then((m) => ({
		default: m.UserEdit,
	})),
);
const UserList = lazy(() =>
	import("@/pages/Settings/UserManagement/UserList").then((m) => ({
		default: m.UserList,
	})),
);
const AllActivity = lazy(() =>
	import("./pages/AllActivity").then((m) => ({ default: m.AllActivity })),
);
const Exports = lazy(() =>
	import("./pages/Exports").then((m) => ({ default: m.Exports })),
);
const CaseManagement = lazy(() =>
	import("./pages/CaseManagement").then((m) => ({ default: m.CaseManagement })),
);
const ManageProfile = lazy(() =>
	import("./pages/Settings/ManageProfile").then((m) => ({
		default: m.ManageProfile,
	})),
);
const AcceptInvite = lazy(() =>
	import("./pages/AcceptInvite").then((m) => ({
		default: m.AcceptInvite,
	})),
);
const AccountActivation = lazy(() =>
	import("./pages/AccountActivation").then((m) => ({
		default: m.AccountActivation,
	})),
);
const SOPManagement = lazy(() =>
	import("./pages/Settings/SOPManagement").then((m) => ({
		default: m.SOPManagement,
	})),
);
const UserInvite = lazy(() =>
	import("./pages/Settings/UserManagement/UserInvite").then((m) => ({
		default: m.UserInvite,
	})),
);
const Support = lazy(() =>
	import("./pages/Support").then((m) => ({ default: m.Support })),
);

const CreateWatch = lazy(() =>
	import("./pages/Watchlist/CreateWatch").then((m) => ({
		default: m.CreateWatch,
	})),
);
const Watchlist = lazy(() =>
	import("./pages/Watchlist/Watchlist").then((m) => ({
		default: m.Watchlist,
	})),
);
const WatchlistItem = lazy(() =>
	import("./pages/Watchlist/WatchlistItem").then((m) => ({
		default: m.WatchlistItem,
	})),
);

// Suspense fallback that tracks loading state - visual handled by LoadingOverlay
// eslint-disable-next-line react-refresh/only-export-components -- Entry point file, not a module
const RouteLoadingFallback = () => <SuspenseTracker />;

// Component that adds app-loaded class once Suspense resolves
// eslint-disable-next-line react-refresh/only-export-components -- Entry point file, not a module
const AppRoutes = () => {
	useEffect(() => {
		document.documentElement.classList.add("app-loaded");
	}, []);

	const { enableAnalytics, enableCaseManagement, enableSopManagement } =
		useNavigationFeatureFlags();

	return (
		<Routes>
			{/* Public routes */}
			<Route element={<AcceptInvite />} path={ROUTES.ACCEPT_INVITE} />
			<Route element={<Login />} path={ROUTES.LOGIN} />
			{/* Protected routes - all nested under ProtectedLayout */}
			<Route element={<ProtectedLayout />}>
				<Route element={<AuthLoginRedirect />} path="/" />
				<Route element={<Dashboard />} path={ROUTES.DASHBOARD} />
				<Route element={<EventDetails />} path={`${ROUTES.EVENTS}/:event_id`} />
				{enableAnalytics && (
					<Route element={<Analytics />} path={ROUTES.ANALYTICS} />
				)}
				{enableCaseManagement && (
					<Route element={<CaseManagement />} path={ROUTES.CASE_MANAGEMENT} />
				)}
				{/* Location management — requires location:access */}
				<Route
					element={<PermissionGuard permission={PERMISSIONS.AccessLocations} />}
				>
					<Route
						element={<LocationsList />}
						path={ROUTES.LOCATION_MANAGEMENT}
					/>
					<Route
						element={<LocationEdit />}
						path={`${ROUTES.LOCATION_MANAGEMENT}/:location_id`}
					/>
				</Route>

				<Route element={<AllActivity />} path={ROUTES.ALL_ACTIVITY} />

				{/* Exports — requires event:export */}
				<Route
					element={<PermissionGuard permission={PERMISSIONS.ExportData} />}
				>
					<Route element={<Exports />} path={ROUTES.EXPORTS} />
				</Route>

				{/* User management — requires user:access */}
				<Route
					element={<PermissionGuard permission={PERMISSIONS.AccessUsers} />}
				>
					<Route element={<UserList />} path={ROUTES.USER_MANAGEMENT} />
					<Route
						element={<PermissionGuard permission={PERMISSIONS.InviteUsers} />}
					>
						<Route element={<UserInvite />} path={ROUTES.USER_INVITE} />
					</Route>
					<Route
						element={
							<PermissionGuard permission={PERMISSIONS.EditUserDetails} />
						}
					>
						<Route
							element={<UserEdit />}
							path={`${ROUTES.USER_MANAGEMENT}/:user_id`}
						/>
					</Route>
				</Route>
				{enableSopManagement && (
					<Route element={<SOPManagement />} path={ROUTES.SOP_MANAGEMENT} />
				)}
				<Route
					element={<AccountActivation />}
					path={ROUTES.ACCOUNT_ACTIVATION}
				/>
				<Route element={<ManageProfile />} path={ROUTES.MANAGE_PROFILE} />
				<Route element={<Support />} path={ROUTES.SUPPORT} />
				<Route
					element={<PermissionGuard permission={PERMISSIONS.AccessWatches} />}
				>
					<Route element={<Watchlist />} path={ROUTES.WATCHLIST} />
					<Route
						element={<WatchlistItem />}
						path={`${ROUTES.WATCHLIST}/:watch_id`}
					/>
					<Route
						element={<PermissionGuard permission={PERMISSIONS.CreateWatch} />}
					>
						<Route element={<CreateWatch />} path={ROUTES.WATCHLIST_CREATE} />
					</Route>
				</Route>
			</Route>

			{/* 404 - Not found */}
			<Route element={<NotFound />} path={ROUTES.NOT_FOUND} />
		</Routes>
	);
};

// Kinde configuration from environment variables
const KINDE_CLIENT_ID = import.meta.env.VITE_KINDE_CLIENT_ID || "";
const KINDE_DOMAIN = import.meta.env.VITE_KINDE_DOMAIN || "";
const KINDE_AUDIENCE = import.meta.env.VITE_KINDE_AUDIENCE || "";

// WebSocket URL from environment variable
const WEBSOCKET_URL = import.meta.env.VITE_WEBSOCKET_URL || "";

installStaleChunkReload();

createRoot(document.getElementById("root")!).render(
	<StrictMode>
		<ErrorBoundary>
			<KindeProvider
				audience={KINDE_AUDIENCE || undefined}
				clientId={KINDE_CLIENT_ID}
				domain={KINDE_DOMAIN}
				logoutUri={window.location.origin}
				redirectUri={window.location.origin}
			>
				<PostHogIdentity />
				<QueryProvider>
					<SettingsProvider>
						<BrowserRouter>
							<EventFiltersProvider>
								<LoadingOverlay />
								<WebSocketProvider websocketUrl={WEBSOCKET_URL}>
									<RouteProvider>
										<NavigationFeatureFlagsProvider>
											<Suspense fallback={<RouteLoadingFallback />}>
												<AppRoutes />
											</Suspense>
										</NavigationFeatureFlagsProvider>
									</RouteProvider>
									<Toaster />
								</WebSocketProvider>
							</EventFiltersProvider>
						</BrowserRouter>
					</SettingsProvider>
				</QueryProvider>
			</KindeProvider>
		</ErrorBoundary>
	</StrictMode>,
);
